import { Field, getIn, useFormikContext } from 'formik';
import { useRef, useImperativeHandle, useCallback } from 'react';
import { css } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { useTheme } from '@hivebrite/design-system/Theme';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';
import { getErrorId, getTooltipId } from '@hivebrite/utils';

import InputLocation from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation';
import { type InputLocationRef } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/types';

import {
  type LocationData,
  type LocationProps,
  type LocationRef,
} from './types';
import { validateLocation } from './validate';

/**
 * Initial values for location input looks like
 * attributeName: {
 *   city: 'Paris',
 *   country: 'France',
 *   country_code: 'FR',
 *   lat: 48.856614,
 *   lng: 2.3522219,
 *   location: 'Paris, France',
 *   location_level: 'locality',
 * },
 */

const Location = ({
  fieldAddressName = 'address',
  hideError = false,
  labelMultiline = true,
  validate = {},
  variant = 'normal',
  wrapperProps = {},
  ariaDescribedBy,
  disabled,
  icon,
  id,
  label,
  name,
  onBlur,
  onChange,
  onlineEnabled,
  onlineLabel,
  onlineName,
  onUpdateOnline,
  placeholder,
  tooltip,
  componentRef,
}: LocationProps & { componentRef?: React.RefObject<LocationRef> }) => {
  const formik = useFormikContext<Record<string, any>>();
  const inputLocationRef = useRef<InputLocationRef>(null);

  const handleChange = (value: LocationData = {}) => {
    formik.setFieldTouched(`${name}[lat]`, true, false);

    formik.setFieldValue(
      `${name}[colloquial_area]`,
      value?.colloquial_area ?? '',
      false,
    );
    formik.setFieldValue(
      `${name}[neighborhood]`,
      value?.neighborhood ?? '',
      false,
    );
    formik.setFieldValue(
      `${name}[administrative_area_level_1]`,
      value?.administrative_area_level_1 ?? '',
      false,
    );
    formik.setFieldValue(
      `${name}[administrative_area_level_2]`,
      value?.administrative_area_level_2 ?? '',
      false,
    );
    formik.setFieldValue(`${name}[provider]`, value?.provider ?? '', false);

    formik.setFieldValue(`${name}[city]`, value?.city ?? '', false);
    formik.setFieldValue(`${name}[country]`, value?.country ?? '', false);
    formik.setFieldValue(
      `${name}[country_code]`,
      value?.country_code ?? '',
      false,
    );
    formik.setFieldValue(`${name}[lat]`, value?.lat ?? '');
    formik.setFieldValue(`${name}[lng]`, value?.lng ?? '', false);
    formik.setFieldValue(`${name}[bounds]`, value?.bounds ?? null, false);
    formik.setFieldValue(
      `${name}[${fieldAddressName}]`,
      value?.location ?? '',
      false,
    );
    formik.setFieldValue(
      `${name}[location_level]`,
      value?.location_level ?? '',
      false,
    );

    if (onChange) {
      onChange(value);
    }

    if (onBlur) {
      onBlur();
    }
  };

  const handleUpdateOnline = (onlineSelected: boolean) => {
    if (!onlineEnabled || !onlineName) {
      return;
    }

    // Set online to true when checked, remove parameter when unchecked
    if (onlineSelected) {
      formik.setFieldValue(`${name}[${onlineName}]`, true);

      // Clear all location fields when online is selected
      formik.setFieldValue(`${name}[lat]`, '');
      formik.setFieldValue(`${name}[lng]`, '');
      formik.setFieldValue(`${name}[city]`, '');
      formik.setFieldValue(`${name}[country]`, '');
      formik.setFieldValue(`${name}[country_code]`, '');
      formik.setFieldValue(`${name}[location_level]`, '');
      formik.setFieldValue(`${name}[colloquial_area]`, '');
      formik.setFieldValue(`${name}[neighborhood]`, '');
      formik.setFieldValue(`${name}[administrative_area_level_1]`, '');
      formik.setFieldValue(`${name}[administrative_area_level_2]`, '');
      formik.setFieldValue(`${name}[provider]`, '');
      formik.setFieldValue(`${name}[bounds]`, null);
      formik.setFieldValue(`${name}[postal_code]`, '');
      formik.setFieldValue(`${name}[sublocality_level_1]`, '');
      formik.setFieldValue(`${name}[${fieldAddressName}]`, '');
    } else {
      formik.setFieldValue(`${name}[${onlineName}]`, undefined);
    }

    // Call external callback to disable map search when online is selected
    if (onUpdateOnline) {
      onUpdateOnline(onlineSelected);
    }

    if (onChange) {
      onChange(null);
    }

    if (onBlur) {
      onBlur();
    }
  };

  const handleCleanTextInput = useCallback(() => {
    if (inputLocationRef.current) {
      inputLocationRef.current.resetLocationState();
    }
  }, []);

  useImperativeHandle(componentRef, () => ({
    handleCleanTextInput,
  }));

  const theme = useTheme();
  const htmlId = id || name;
  const errorId = getErrorId(htmlId);
  const tooltipId = getTooltipId(htmlId);
  const formikValue = getIn(formik.values, name);
  const formikLatError = getIn(formik.errors, `${name}[lat]`);
  const formikLatTouched = getIn(formik.touched, `${name}[lat]`);
  const formikOnlineValue = onlineName
    ? getIn(formik.values, `${name}[${onlineName}]`)
    : undefined;
  const inputHeight = theme?.input?.[variant]?.height || 40;
  const normalBorderColor = theme?.input?.[variant]?.borderColor || '#737373';
  const focusBorderColor =
    theme?.input?.[variant]?.focus?.borderColor || theme?.colors?.primary;
  const errorBorderColor =
    theme?.input?.[variant]?.error?.borderColor || '#FD4329';
  const borderRadius = theme?.input?.[variant]?.borderRadius || 4;
  const borderColor =
    formikLatTouched && formikLatError ? errorBorderColor : normalBorderColor;

  const locationInputCss = css`
    height: ${inputHeight}px;
    border: 1px solid ${borderColor} !important;
    border-radius: ${borderRadius}px !important;

    &:focus {
      border: 1px solid ${focusBorderColor} !important;
    }
  `;

  return (
    <Box
      display="block"
      mb={6}
      opacity={disabled ? theme?.input?.disabled?.opacity || 1 : 1}
      {...wrapperProps}
    >
      <Label
        id={htmlId}
        disabled={disabled}
        multiline={labelMultiline}
        required={validate.required}
      >
        {label}
      </Label>

      <Field
        type="hidden"
        name={`${name}[lat]`}
        validate={(value) =>
          validateLocation({
            required: validate.required,
            value,
          })
        }
        data-testid="formik-location"
      />
      <Tooltip initialPosition={POSITIONS.TOP} childrenElement={tooltip}>
        <InputLocation
          {...((formikLatError || ariaDescribedBy || tooltip) && {
            ariaDescribedBy: [
              formikLatError && errorId,
              ariaDescribedBy,
              tooltip && tooltipId,
            ]
              .filter(Boolean)
              .join(' '),
          })}
          componentRef={inputLocationRef}
          clearable
          defaultLocation={formikValue}
          defaultOnline={!!formikOnlineValue}
          disabled={disabled}
          icon={icon}
          id={htmlId}
          inputName="address"
          onlineEnabled={onlineEnabled}
          {...(onlineEnabled &&
            onlineLabel && {
              onlineLabel,
              onUpdateOnline: handleUpdateOnline,
            })}
          onSelectLocation={handleChange}
          placeholder={placeholder}
          primaryInputName={fieldAddressName}
          inputHtml={{
            css: locationInputCss,
          }}
        />
      </Tooltip>
      {/* Make tooltip also available for ScreenReaders */}
      {tooltip && (
        <ScreenReadersContent id={tooltipId}>
          {typeof tooltip === 'function' ? tooltip() : tooltip}
        </ScreenReadersContent>
      )}
      {!hideError &&
        (formikLatTouched || formik.submitCount > 0) &&
        formikLatError && (
          <AdditionalInformation id={htmlId}>
            {formikLatError}
          </AdditionalInformation>
        )}
    </Box>
  );
};

Location.displayName = 'Location';

export default Location;
